import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import type { Control, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { createPersonRequestViewModelSchema } from '@/shared/api/generated/schemas';
import { isValidCpf } from '@/shared/utils/cpf';
import { getRGFullError, type RGErrorKey } from '@/shared/utils/rg';

const FOREIGN_DOCUMENT_FORBIDDEN_CHARS = /[+=*%!?@#$&"_(),:;\p{Extended_Pictographic}]/u;

// ─── Sub-schemas (estrutura pura, sem mensagens de erro) ──────────────────────

export const studentSchema = z.object({
  ra: z.string().nullish(),
  curriculum: z.string().nullish(),
  codeUsp: z.string().nullish(),
  perfilPublic: z.boolean().optional(),
});

export const employeeSchema = z.object({
  postId: z.number().int().nullish(),
  codeBadge: z.string().nullish(),
  dateStart: z.string().nullish(),
  dateFinish: z.string().nullish(),
  type: z.number().int().nullish(),
  serviceRequestNotification: z.boolean().optional(),
});

export const teachingStaffSchema = z.object({
  observation: z.string().nullish(),
  isUspTeaching: z.boolean().optional(),
  codeUSP: z.string().nullish(),
  certNumber: z.string().nullish(),
  uspContractType: z.number().int().nullish(),
  academicEducation: z.number().int().nullish(),
  certWorkload: z.number().nullish(),
  certExpirationDate: z.string().nullish(),
  isLattes: z.boolean().optional(),
  lattesUpdateDate: z.string().nullish(),
});

// ─── Address schema factory ───────────────────────────────────────────────────

export const buildAddressSchema = (t: TFunction<'personManagement'>) =>
  z.object({
    street: z.string().trim().max(200, t('personForm.validation.addressStreetMax')).nullish(),
    zipCode: z.string().nullish(),
    placeNumber: z.string().trim().max(30, t('personForm.validation.addressNumberMax')).nullish(),
    neighborhood: z
      .string()
      .trim()
      .max(100, t('personForm.validation.addressNeighborhoodMax'))
      .nullish(),
    complement: z
      .string()
      .trim()
      .max(120, t('personForm.validation.addressComplementMax'))
      .nullish(),
    countryId: z.number().int().nullish(),
    stateId: z.number().int().nullish(),
    cityId: z.number().int().nullish(),
    stateName: z.string().trim().max(100, t('personForm.validation.addressStateMax')).nullish(),
    cityName: z.string().trim().max(100, t('personForm.validation.addressCityMax')).nullish(),
  });

export type AddressFormValues = z.infer<ReturnType<typeof buildAddressSchema>>;

// ─── Person form schema factory ───────────────────────────────────────────────

export const buildPersonFormSchema = (t: TFunction<'personManagement'>) => {
  const rgErrorMessages: Record<RGErrorKey, string> = {
    prefix_too_long: t('personForm.validation.rgPrefixTooLong'),
    x_not_at_end: t('personForm.validation.rgXNotAtEnd'),
    letters_in_middle: t('personForm.validation.rgLettersInMiddle'),
    too_short: t('personForm.validation.rgTooShort'),
    too_long: t('personForm.validation.rgTooLong'),
    identical_digits: t('personForm.validation.rgIdenticalDigits'),
  };

  const addressSchema = buildAddressSchema(t);

  return createPersonRequestViewModelSchema
    .pick({
      firstName: true,
      lastName: true,
      email: true,
      cpf: true,
      celPhone: true,
      homePhone: true,
      workPhone: true,
      birthDate: true,
      gender: true,
      observation: true,
      isStudent: true,
      isEmployee: true,
      isTeachingStaff: true,
      rg: true,
      dispatcherOrgan: true,
      dispatcherOrganState: true,
      dateDispatch: true,
      passportNumber: true,
    })
    .extend({
      // Overrides de validação
      firstName: z.string().min(1, t('personForm.validation.firstNameRequired')),
      lastName: z.string().min(1, t('personForm.validation.lastNameRequired')),
      email: z
        .string()
        .min(1, t('personForm.validation.emailRequired'))
        .email(t('personForm.validation.emailInvalid')),
      cpf: z
        .string()
        .min(1, t('personForm.validation.cpfRequired'))
        .refine(v => v.replace(/\D/g, '').length === 11, t('personForm.validation.cpfLength'))
        .refine(v => isValidCpf(v), t('personForm.validation.cpfInvalid')),
      birthDate: z.string().nullish(),
      dateDispatch: z.string().nullish(),
      gender: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
      observation: z.string().nullish(),
      isStudent: z.boolean().optional(),
      isEmployee: z.boolean().optional(),
      isTeachingStaff: z.boolean().optional(),

      // Pessoal
      alias: z.string().nullish(),
      martialStatus: z.number().int().optional(),
      nationalityId: z.number().int().optional(),
      birthCountryId: z.number().int().optional(),
      birthStateId: z.number().int().optional(),
      birthCityId: z.number().int().optional(),
      birthStateName: z.string().nullish(),
      birthCityName: z.string().nullish(),
      mothersName: z.string().nullish(),
      fathersName: z.string().nullish(),
      spouseName: z.string().nullish(),

      // Contato
      receiveSms: z.boolean().optional(),
      receiveMarketing: z.boolean().optional(),
      miniCurriculum: z.string().nullish(),
      urlLinkedin: z.string().nullish(),
      urlLattes: z.string().nullish(),

      // Documentos
      dispatcherOrganState: z
        .string()
        .trim()
        .max(2, t('personForm.validation.dispatcherOrganStateMax'))
        .nullish(),
      passportNumber: z
        .string()
        .trim()
        .max(100, t('personForm.validation.passportMaxLength'))
        .nullish(),
      rg: z
        .string()
        .nullish()
        .superRefine((v, ctx) => {
          if (!v) return;
          const error = getRGFullError(v);
          if (error) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: rgErrorMessages[error] });
          }
        }),
      idCountryNumber: z
        .string()
        .trim()
        .max(100, t('personForm.validation.foreignDocumentMaxLength'))
        .refine(
          v => !v || !FOREIGN_DOCUMENT_FORBIDDEN_CHARS.test(v),
          t('personForm.validation.foreignDocumentSpecialChars'),
        )
        .nullish(),

      // Profissional
      profession: z.string().nullish(),
      professionId: z.number().int().optional(),
      company: z.string().nullish(),
      associatedCompanyId: z.number().int().optional(),
      jobPositionId: z.number().int().optional(),
      otherJobPosition: z.string().nullish(),
      businessSectorId: z.number().int().optional(),
      otherBusinessSector: z.string().nullish(),
      serviceTime: z.number().int().optional(),
      experienceLevel: z.number().int().optional(),
      selfEmployed: z.boolean().optional(),
      isWorking: z.boolean().optional(),

      // Acadêmico
      academicEducation: z.number().int().optional(),
      academicInstitutionId: z.number().int().optional(),
      academicCourseName: z.string().nullish(),
      completionDate: z.string().nullish(),

      // Perfis
      isMonitor: z.boolean().optional(),

      // Sub-entidades de perfil
      student: studentSchema.optional(),
      employee: employeeSchema.optional(),
      teachingStaff: teachingStaffSchema.optional(),

      // Acessibilidade
      isPCD: z.boolean().optional(),
      pcdDescription: z.string().nullish(),
      useAccessibilityTool: z.boolean().optional(),
      accessibilityToolDescription: z.string().nullish(),

      // Endereços
      personalAddress: addressSchema.optional(),
      paymentAddress: addressSchema.optional(),

      // Financeiro (campos flat para evitar objeto aninhado no payload)
      financialBankName: z.string().nullish(),
      financialAgency: z.string().nullish(),
      financialCheckingAccount: z.string().nullish(),
      financialSavingAccount: z.string().nullish(),
      financialPaymentMethod: z.number().int().optional(),
      financialAccountType: z.number().int().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.isPCD === true && !data.pcdDescription?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('personForm.validation.pcdDescriptionRequired'),
          path: ['pcdDescription'],
        });
      }

      if (data.useAccessibilityTool === true && !data.accessibilityToolDescription?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('personForm.validation.accessibilityToolDescriptionRequired'),
          path: ['accessibilityToolDescription'],
        });
      }
    });
};

export type PersonFormValues = z.infer<ReturnType<typeof buildPersonFormSchema>>;

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Hook que instancia o schema de validação do formulário de pessoa com as
 * mensagens de erro traduzidas para o idioma atual.
 */
export const usePersonFormSchema = () => {
  const { t } = useTranslation('personManagement');
  return useMemo(() => buildPersonFormSchema(t), [t]);
};

// ─── Props compartilhadas entre tabs ─────────────────────────────────────────

export interface TabProps {
  readonly control: Control<PersonFormValues>;
  readonly register: UseFormRegister<PersonFormValues>;
  readonly errors: FieldErrors<PersonFormValues>;
  readonly setValue?: UseFormSetValue<PersonFormValues>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function numToStr(v: number | null | undefined): string {
  return v !== null && v !== undefined ? String(v) : '';
}

export function strToNum(v: string): number | undefined {
  const n = Number(v);
  return v === '' || Number.isNaN(n) ? undefined : n;
}
